import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import DataFrameFilterCreator, {
  FilterOptionsForCreator,
} from 'data-frames/filters/components/DataFrameFilterCreator';
import {DataFrameFilterOptions} from 'data-frames/filters/DataFrameFilterOptions';
import {DataFrameFilter} from 'data-frames/filters/DataFrameFilterTypes';
import findColumn from 'data-frames/util/findColumn';
import getUserColumns from 'data-frames/util/getUserColumns';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import * as Colors from 'ui-library/Colors';
import Float, {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

export type FilterOptionsForPopover = Pick<
  DataFrameFilterOptions,
  'getFilterForColumnHeaders' | 'dataFrameSpec' | 'getUnfilteredColumns'
> &
  FilterOptionsForCreator;
export interface FilterEditorPopoverProps {
  children: (openMenu: () => void, isDisabled: boolean) => JSX.Element;
  // Specify our own filter options, because different callers will pass in different getters/setters
  filterOptions: FilterOptionsForPopover;
  /**
   * Where to position the floating element, relative to its child.
   *
   * @default FloatingPlacement.BOTTOM_END
   */
  floatingPlacement?: FloatingPlacement;
  initialFilter?: DataFrameFilter;
  canSelectColumn?: boolean;
}

export default function FilterEditorPopover({
  children,
  filterOptions,
  floatingPlacement,
  initialFilter,
  canSelectColumn = true,
}: FilterEditorPopoverProps): JSX.Element {
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const userColumns = getUserColumns(filterOptions.dataFrameSpec);
  const selectedColumnDef = getInitialSelectedColumn();

  function getInitialSelectedColumn(): DataFrameColumnDef | undefined {
    const filterColumn =
      initialFilter != null
        ? findColumn(filterOptions.dataFrameSpec, initialFilter.filters[0].columnSystemName)
        : null;

    if (filterColumn == null) {
      const unfilteredColumn = userColumns.find((column) =>
        filterOptions
          ?.getUnfilteredColumns(filterOptions.dataFrameSpec)
          .map((uc) => uc.systemName)
          .includes(column.systemName)
      );

      return unfilteredColumn;
    }

    return filterColumn;
  }

  return (
    <Float
      isOpen={isEditorOpen}
      onRequestClose={() => {
        setIsEditorOpen(false);
      }}
      interaction={FloatingInteraction.CONTROLLED}
      placement={floatingPlacement ?? FloatingPlacement.BOTTOM_END}
      floatingElementOptions={{
        floatingBodyZIndex: ZINDEX_MODAL,
        floatingBodyClassName: css(styles.popdownBody),
      }}
      toggleDisplayMode={ToggleDisplayMode.BLOCK_FIT_CONTENT}
      renderContent={() =>
        selectedColumnDef ? (
          <div className={css(styles.filterPopDownContent)}>
            <DataFrameFilterCreator
              canSelectColumn={canSelectColumn}
              columns={userColumns}
              filterOptionsForCreator={filterOptions}
              initialSelectedColumnName={selectedColumnDef.systemName}
              onCancel={() => {
                setIsEditorOpen(false);
              }}
              onSave={() => {
                setIsEditorOpen(false);
              }}
            />
          </div>
        ) : null
      }
    >
      {children(() => {
        setIsEditorOpen(true);
      }, selectedColumnDef == null)}
    </Float>
  );
}

const styles = StyleSheet.create({
  filterPopDownContent: {
    padding: 10,
    minWidth: 300,
    maxWidth: 500,
  },
  popdownBody: {
    background: Colors.WHITE,
    borderRadius: 4,
    boxShadow: `0px 2px 4px ${Colors.GRAY_DARKER}`,
  },
});
